import React, { useState } from 'react';
import { User } from 'lucide-react';

interface UsernameModalProps {
  onSubmit: (username: string) => void;
  isDarkMode: boolean;
}

export const UsernameModal: React.FC<UsernameModalProps> = ({ onSubmit, isDarkMode }) => {
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate username
    const trimmed = username.trim();
    if (trimmed.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }
    if (trimmed.length > 20) {
      setError('Username must be 20 characters or less');
      return;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
      setError('Username can only contain letters, numbers, hyphens, and underscores');
      return;
    }
    
    onSubmit(trimmed);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className={`w-full max-w-md p-6 rounded-2xl shadow-2xl ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="flex flex-col items-center mb-6">
          <div className={`p-4 rounded-full mb-4 ${
            isDarkMode ? 'bg-blue-900 text-blue-400' : 'bg-blue-100 text-blue-600'
          }`}>
            <User size={32} />
          </div>
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            Choose Your Username
          </h2>
          <p className={`text-sm mt-2 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            This will be displayed on the leaderboard
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="text"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setError('');
              }}
              placeholder="Enter username"
              className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              } ${error ? 'border-red-500' : ''}`}
              maxLength={20}
              autoFocus
            />
            {error && (
              <p className="mt-2 text-sm text-red-500">{error}</p>
            )}
            <p className={`mt-2 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              3-20 characters, letters, numbers, hyphens, and underscores only
            </p>
          </div>

          <button
            type="submit"
            className={`w-full px-6 py-3 rounded-lg font-bold text-lg shadow-lg hover:shadow-xl active:scale-95 transition-all ${
              isDarkMode 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-slate-900 text-white hover:bg-slate-800'
            }`}
          >
            Continue
          </button>
        </form>
      </div>
    </div>
  );
};
